import { upstreamBuilder } from './builder-upstream.js';
import type {
  BaseEndpointOptions,
  BuilderConfig,
  BuilderInstance,
  EndpointOptions,
} from './types.js';

/**
 * Mint-flavored builder. Thin wrapper over upstream.
 *
 * MINT-OVERRIDE:
 *   1. `prefixUrl` is prepended to every declared endpoint's URL.
 *   2. Legacy top-level `snakeCaseQueryParams` on an endpoint is normalized into
 *      `clientOptions.request.snakeCaseQueryParams` for the @mint-lib/api middleware.
 */
export function builder<UseDiscriminator extends boolean = false>(
  config: BuilderConfig<UseDiscriminator> = {} as BuilderConfig<UseDiscriminator>,
): BuilderInstance<UseDiscriminator> {
  const upstream = upstreamBuilder<UseDiscriminator>(config as never);
  const prefixUrl = config.prefixUrl ?? '';

  function normalize<O extends BaseEndpointOptions>(options: O): O {
    const withUrl = { ...options, url: prefixUrl + options.url };
    const legacy = (options as { snakeCaseQueryParams?: boolean })
      .snakeCaseQueryParams;
    if (legacy) {
      const existingClientOptions =
        (options as { clientOptions?: { request?: unknown } }).clientOptions ??
        {};
      const clientOptions = {
        ...existingClientOptions,
        request: {
          ...((existingClientOptions.request as object) ?? {}),
          snakeCaseQueryParams: true,
        },
      };
      return { ...withUrl, clientOptions } as O;
    }
    return withUrl as O;
  }

  return {
    ...upstream,
    declareEndpoint: ((options: EndpointOptions) =>
      upstream.declareEndpoint(
        normalize(options),
      )) as unknown as BuilderInstance<UseDiscriminator>['declareEndpoint'],
    declareMultipart: ((options: EndpointOptions) =>
      upstream.declareMultipart(
        normalize(options),
      )) as unknown as BuilderInstance<UseDiscriminator>['declareMultipart'],
    declareStream: ((options: BaseEndpointOptions) =>
      upstream.declareStream(
        normalize(options),
      )) as unknown as BuilderInstance<UseDiscriminator>['declareStream'],
  };
}
